import { useState, useRef } from 'react'


/**
 * Custom hook for interacting with the AI movie assistant API
 * 
 * Features:
 * - Automatic retry logic (up to 2 attempts)
 * - Loading and error state management
 * - Message length validation (max 500 characters)
 * 
 * @returns Object containing:
 *   - getAnswer(message, threadId?, accessToken?) - Send message and get AI response
 *   - isLoading - Request in progress indicator
 *   - errorMessage - Error from most recent request
 *   - setErrorMessage - Manually set/clear errors
 * 
 * @example
 * const { getAnswer, isLoading } = useGetAnswerV2()
 * const response = await getAnswer("What's playing?", threadId)
 */

export const useGetAnswerV2 = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const attempts = useRef(0)
  const aiToolApiUrl =
    process.env.NODE_ENV === 'production'
      ? 'https://discovery.tamedia.ch/ai-tools/api/v2/movie-assistant'
      : 'https://discovery-dev.tamedia.ch/ai-tools/api/v2/movie-assistant'

  const getAnswer = async (
    message: string,
    threadId?: string | null | undefined,
  ): Promise<ChatInterface> => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await fetch(aiToolApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-okta': 'disabled',
        },
        body: JSON.stringify({
          message: message,
          thread_id: threadId,
        }),
      })

      // If the response is not ok, try again with a fresh access token
      if (!response.ok) {
        // Check if the message is too long
        const errorObj = await response.json()

        if (
          errorObj.msg ===
          'Message is bigger than max allowed length of 500 characters.'
        ) {
          throw new Error(errorObj.msg)
        }

        // If the response is unauthorized, try again
        if (attempts.current < 2) {
          attempts.current += 1
          return getAnswer(message, threadId)
          // If the response is still unauthorized, throw an error
        } else {
          attempts.current = 0
          throw new Error(errorObj.error || 'Failed to fetch answer')
        }
      }

      const data = await response.json()
      attempts.current = 0
      setIsLoading(false)

      const date = new Date()
      return {
        id: data.generation_id,
        text: data.text,
        articles: data.unity_resources || [],
        type: 'bot' as 'bot',
        date: date,
        threadId: data.thread_id,
      }
    } catch (error: any) {
      setIsLoading(false)
      setErrorMessage(error.message || 'An error occurred')
      return Promise.reject(error)
    }
  }

  return {
    getAnswer,
    isLoading,
    errorMessage,
    setErrorMessage,
  }
}
